import { useEffect, useRef, useState } from 'react';
import { VideoAreaClip } from '../types';

export type RotatingClip = {
  type: 'video' | 'image';
  imageUrl?: string; // Current image url (sticky)
  videoUrl?: string; // Current video url (sticky)
};

/**
 * Rotates through a list of clips based on each clip's `duration`.
 * If a clip has no duration, rotation pauses on that clip (unlimited).
 */
export function useRotatingClip(clips: VideoAreaClip[] = []): RotatingClip {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef<number | null>(null);

  // Persist last seen URLs without causing extra renders on every change
  const stickyUrlsRef = useRef<{ imageUrl?: string; videoUrl?: string }>({});

  // Compute current clip with clamped index
  const hasClips = clips.length > 0;
  const safeIdx = hasClips ? Math.max(0, Math.min(idx, clips.length - 1)) : 0;
  const current = hasClips ? clips[safeIdx] : undefined;

  // Advance timer handling
  useEffect(() => {
    // reset timer
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!current) return;

    const hasDuration =
      typeof current.duration === 'number' && current.duration > 0;

    if (hasDuration && clips.length > 0) {
      timerRef.current = window.setTimeout(() => {
        setIdx((prev) => (prev + 1) % clips.length);
      }, current.duration);
    }

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [current, clips.length]);

  // React 19.2
  // useEffect(() => {
  //     // Preload next item
  //     const nextIdx = (idx + 1) % clips.length;
  //     const nextClip = clips[nextIdx];
  //     if (nextClip?.url) {
  //       preload(nextClip.url, {as: nextClip.type});
  //     }
  // }, [idx]);

  // Update sticky URLs for crossfade effects
  useEffect(() => {
    if (!current) return;
    if (current.type === 'image') {
      stickyUrlsRef.current.imageUrl = current.url;
    } else {
      stickyUrlsRef.current.videoUrl = current.url;
    }
  }, [current]);

  // If no clips, return defaults
  if (!current) {
    return {
      type: 'image',
      imageUrl: stickyUrlsRef.current.imageUrl,
      videoUrl: stickyUrlsRef.current.videoUrl,
    };
  }

  return {
    type: current.type,
    imageUrl: stickyUrlsRef.current.imageUrl,
    videoUrl: stickyUrlsRef.current.videoUrl,
  };
}
